/** IAM region codes (from verify-otp /auth/me). */
export type RegionCode = 'IN' | 'GCC'

/** UI view: single region or global fan-out. */
export type RegionView = RegionCode | 'GLOBAL'

export type IamRegion = {
  region_code: string
  region_name?: string
  is_primary?: boolean
}

export const REGION_LABELS: Record<RegionCode, string> = {
  IN: 'India',
  GCC: 'GCC',
}

export const ALL_REGION_CODES: RegionCode[] = ['IN', 'GCC']

function normalizeRegionCode(raw: unknown): RegionCode | null {
  if (typeof raw !== 'string') return null
  const c = raw.trim().toUpperCase()
  if (c === 'IN' || c === 'IND' || c === 'INDIA') return 'IN'
  if (c === 'GCC') return 'GCC'
  return null
}

/** Parse IAM user payload → entitled region codes (stable order IN, GCC). */
export function parseUserRegionCodes(user: unknown): RegionCode[] {
  if (user == null || typeof user !== 'object') return []
  const u = user as Record<string, unknown>
  const found = new Set<RegionCode>()

  const codes = u.region_codes
  if (Array.isArray(codes)) {
    for (const c of codes) {
      const n = normalizeRegionCode(c)
      if (n) found.add(n)
    }
  }

  const regions = u.regions
  if (Array.isArray(regions)) {
    for (const r of regions) {
      if (r && typeof r === 'object') {
        const n = normalizeRegionCode((r as IamRegion).region_code)
        if (n) found.add(n)
      }
    }
  }

  const primary = normalizeRegionCode(u.primary_region)
  if (primary) found.add(primary)

  return ALL_REGION_CODES.filter((c) => found.has(c))
}

export function parsePrimaryRegion(user: unknown): RegionCode | null {
  if (user == null || typeof user !== 'object') return null
  const u = user as Record<string, unknown>
  const fromField = normalizeRegionCode(u.primary_region)
  if (fromField) return fromField

  const regions = u.regions
  if (Array.isArray(regions)) {
    for (const r of regions) {
      if (r && typeof r === 'object' && (r as IamRegion).is_primary) {
        const n = normalizeRegionCode((r as IamRegion).region_code)
        if (n) return n
      }
    }
  }

  const codes = parseUserRegionCodes(user)
  return codes[0] ?? null
}

function cleanBase(raw: unknown): string {
  if (raw == null || raw === '') return ''
  const trimmed = String(raw).trim()
  if (trimmed === '/' || trimmed.toLowerCase() === 'same-origin') return ''
  return trimmed.replace(/\/+$/, '')
}

/**
 * Regional Adonis API bases.
 * Prefer VITE_API_URL_IN / VITE_API_URL_GCC; fall back to legacy VITE_API_URL for IN.
 */
export function getRegionApiBaseUrls(): Partial<Record<RegionCode, string>> {
  const legacy = cleanBase(import.meta.env.VITE_API_URL)
  const ind = cleanBase(import.meta.env.VITE_API_URL_IN) || legacy
  const gcc = cleanBase(import.meta.env.VITE_API_URL_GCC)
  const out: Partial<Record<RegionCode, string>> = {}
  if (ind) out.IN = ind
  if (gcc) out.GCC = gcc
  return out
}

/** Shared tag engine (same for IN + GCC). */
export function getEngineApiBaseUrl(): string {
  return cleanBase(import.meta.env.VITE_ENGINE_API_URL)
}

/** Auth BFF: prefer IN, then GCC, then empty (same-origin). */
export function getAuthApiBaseUrl(): string {
  const bases = getRegionApiBaseUrls()
  return bases.IN || bases.GCC || ''
}

/** Regions that have a configured API URL and the user is entitled to. */
export function resolveAvailableRegions(entitled: RegionCode[]): RegionCode[] {
  const configured = getRegionApiBaseUrls()
  const withUrl = ALL_REGION_CODES.filter((c) => Boolean(configured[c]))
  if (entitled.length === 0) {
    // No IAM regions yet (pre-login / incomplete me) — expose whatever is configured
    return withUrl
  }
  return entitled.filter((c) => withUrl.includes(c))
}

export function canUseGlobalView(available: RegionCode[]): boolean {
  return available.includes('IN') && available.includes('GCC')
}

/**
 * Draft CUSTOUT / RFID Validation Report exists only on the India backend.
 * Show when the user has IN access and is not viewing GCC-only.
 */
export function canAccessCustoutValidation(
  view: RegionView,
  availableRegions: RegionCode[]
): boolean {
  if (!availableRegions.includes('IN')) return false
  return view === 'IN' || view === 'GLOBAL'
}
